import { ContractMenuList, Option, ValueContainer } from "components/shared/baseBidForm";
import { CargoOwnerDocsForm } from "components/shared/cargoOwnerDocs";
import { counterpartyInsolvency } from "helpers/bid";
import { distributionBidOptions, distributionBidOptionsEnum, featureModuleTypesEnum } from "helpers/enums";
import { HiddenDataWrapper } from "helpers/hiddenData";
import { flagVal } from "helpers/system";
import React, { Fragment, useEffect, useState } from "react";
import { list as counterpartyContracts } from "sources/books/counterpartyContracts";
import { count } from "sources/books/legalPersons";
import PaymentFields from "../../paymentFields";

const isPreBidFlagVal = flagVal(distributionBidOptions, distributionBidOptionsEnum.IsPreBid);

const MainData = ({
    bidPolicy,
    renderField,
    renderSelect,
    renderCheckbox,
    renderDateField,
    config,
    priceWithoutVatOnTop,
    isVatTop,
    documentsExists,
    documents,
    setValue,
    profile,
    modal,
    roles,
    organization,
    temperatureRegimeEnabled,
    temperatureRegime,
    counterpartyComment,
    counterpartyInsolvencyRegistryEntry,
    counterpartyId,
    legalPersonId,
    values,
    renderExpressBid,
    renderContactPerson,
    renderHiredCar,
    renderExtendedFields,
}) => {
    const [legalPersonCount, setLegalPersonCount] = useState(0);
    useEffect(() => {
        count().then((res) => setLegalPersonCount(res.count || 0));
    }, []);
    const { temperatureMinimum, temperatureMaximum } = config.temperatureRegime.config;
    const getTemperatureRegimeProps = (temperatureConfig) => ({
        ...temperatureConfig,
        validators: [
            (value) => temperatureConfig.validators.map((validator) => validator(value, { temperatureRegime })),
        ],
        value:
            temperatureRegime[temperatureConfig.inputProps.name] != null
                ? temperatureRegime[temperatureConfig.inputProps.name]
                : "",
        onChange: ({ name, value }) =>
            setValue({ name: "temperatureRegime", value: { ...temperatureRegime, [name]: value } }),
        fieldWrapper: "col-sm-8 col-md-5",
        bWrapper: "row form-group",
    });
    const renderDocuments = () => {
        return (
            <div className="row form-group">
                <label className="col-sm-4 col-md-3 col-form-label col-form-label-sm text-md-right text-sm-left">
                    Документы
                </label>
                <div className="col-sm-8 col-md-9">
                    <CargoOwnerDocsForm documents={documents} setValue={setValue} />
                </div>
            </div>
        );
    };

    const isPreBid = (values.intOptions & isPreBidFlagVal) === isPreBidFlagVal;

    return (
        <>
            <div className="form-group">
                <strong>Общие параметры</strong>
            </div>
            {renderSelect({
                ...config.responsibleId,
                disabled: !bidPolicy.canChangeResponsible(),
                fieldWrapper: "col-sm-8 col-md-5",
                bWrapper: "row form-group",
            })}
            {legalPersonCount >= 1 &&
                renderSelect({
                    ...config.legalPersonId,
                    fieldWrapper: "col-sm-8 col-md-5",
                    bWrapper: "row form-group",
                })}
            <HiddenDataWrapper>
                {renderSelect({
                    ...config.counterpartyId,
                    modal,
                    roles,
                    profile,
                    organization,
                    fieldWrapper: "col-sm-8 col-md-5",
                    bWrapper: "row form-group",
                    inputBottomText: (
                        <>
                            <div>{counterpartyInsolvency(counterpartyInsolvencyRegistryEntry)}</div>
                            <small>
                                <i className="text-muted">{counterpartyComment}</i>
                            </small>
                        </>
                    ),
                })}
            </HiddenDataWrapper>
            <Fragment key={`${counterpartyId}_${legalPersonId}`}>
                {renderSelect({
                    ...config.contractId,
                    fieldWrapper: "col-sm-8 col-md-5",
                    bWrapper: "row form-group",
                    disabled: !counterpartyId,
                    components: { ValueContainer, Option, MenuList: ContractMenuList },
                    counterpartyId,
                    legalPersonId,
                    modal,
                    organization,
                    roles,
                    url: (params) => counterpartyContracts({ counterpartyId, legalPersonId, ...params }),
                })}
            </Fragment>
            {renderField({
                ...config.contractNumber,
                fieldWrapper: "col-sm-8 col-md-5",
                bWrapper: "row form-group",
            })}
            {documentsExists && renderDocuments()}
            {renderSelect({
                ...config.trailerTypeId,
                fieldWrapper: "col-sm-8 col-md-5",
                bWrapper: "row form-group",
            })}
            {renderField({
                ...config.comment,
                fieldWrapper: "col-sm-8 col-md-5",
                bWrapper: "row form-group",
            })}
            {renderExpressBid()}
            {renderCheckbox({
                ...config.isMandatory,
                fieldWrapper: "col-sm-8 col-md-5",
                bWrapper: "row form-group",
            })}
            {config.isPreBid &&
                !config.isPreBid.isHidden &&
                renderCheckbox({
                    ...config.intOptions,
                    fieldWrapper: "col-sm-8 col-md-5",
                    bWrapper: "row form-group",
                    onChange: ({ name }) =>
                        setValue({
                            name,
                            value: isPreBid ? values.intOptions - isPreBidFlagVal : values.intOptions + isPreBidFlagVal,
                        }),
                    value: isPreBid,
                })}
            {temperatureRegimeEnabled && (
                <>
                    <Fragment key={`temperatureMinimum_${temperatureRegime.temperatureMaximum}`}>
                        {renderField({ ...getTemperatureRegimeProps(temperatureMinimum) })}
                    </Fragment>
                    <Fragment key={`temperatureMaximum_${temperatureRegime.temperatureMinimum}`}>
                        {renderField({ ...getTemperatureRegimeProps(temperatureMaximum) })}
                    </Fragment>
                </>
            )}
            {profile.availableFeatureModules.includes(featureModuleTypesEnum.DocumentAssignment) &&
                renderCheckbox({
                    ...config.createDocumentAssignment,
                    fieldWrapper: "col-sm-8 col-md-5",
                    bWrapper: "row form-group",
                })}
            {renderContactPerson()}
            {renderField({ ...config.clientBidNumber, fieldWrapper: "col-sm-8 col-md-5", bWrapper: "row form-group" })}
            {renderDateField({ ...config.clientBidDate, wrapper: "col-sm-8 col-md-5", bWrapper: "row form-group" })}
            {renderExtendedFields()}
            <div className="form-group mt-3">
                <strong>Оплата</strong>
            </div>
            {renderSelect({
                ...config.paymentTypeId,
                fieldWrapper: "col-sm-8 col-md-5",
                bWrapper: "row form-group",
            })}
            {renderSelect({ ...config.paymentPeriodType, fieldWrapper: "col-sm-8 col-md-5" })}
            {renderField({ ...config.paymentPeriodInDays, fieldWrapper: "col-sm-8 col-md-5" })}
            {renderSelect({ ...config.invoiceTriggerType, fieldWrapper: "col-sm-8 col-md-5" })}
            {values.hasHiredCar &&
                profile.availableFeatureModules.includes(featureModuleTypesEnum.AccountsReceivable) && (
                    <div className="row">
                        <div className="offset-sm-4 offset-md-3 col-sm-8 col-md-5">
                            <div className="card card-body mb-3">
                                <strong className="mb-2">Расчёты с заказчиком</strong>
                                <PaymentFields
                                    payment={values.customerPayment}
                                    onChange={(value) => setValue({ name: "customerPayment", value })}
                                    renderDateField={renderDateField}
                                    renderField={renderField}
                                    renderSelect={renderSelect}
                                />
                            </div>
                        </div>
                    </div>
                )}
            <HiddenDataWrapper>
                {isVatTop && priceWithoutVatOnTop
                    ? renderField({
                          ...config.priceWithoutVatOnTop,
                          fieldWrapper: "col-sm-8 col-md-5",
                          bWrapper: "row form-group",
                      })
                    : renderField({
                          ...config.price,
                          fieldWrapper: "col-sm-8 col-md-5",
                          bWrapper: "row form-group",
                      })}
            </HiddenDataWrapper>
            {renderSelect({
                ...config.ndsTypeId,
                fieldWrapper: "col-sm-8 col-md-5",
                bWrapper: "row form-group",
            })}
            {renderCheckbox({
                ...config.isVatTop,
                fieldWrapper: "col-sm-8 col-md-5",
                bWrapper: "row form-group",
            })}
            {renderHiredCar()}
        </>
    );
};

export default MainData;
